"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import Script from "next/script";
import { useSearchParams, useRouter } from "next/navigation";
import {
  Check,
  X,
  Sparkles,
  ShieldCheck,
  Server,
  MessageSquare,
  ArrowRight,
  HelpCircle,
  Zap,
  Users,
  ChevronDown,
  ChevronUp,
  PhoneCall,
  ExternalLink,
  Calculator,
  RefreshCw,
  Layers,
  Database,
  Lock,
  Headphones,
  CheckCircle2,
  FileText,
  CreditCard,
  Building2,
  Sliders,
  DollarSign,
  Code2,
  Workflow,
  Compass,
  Briefcase,
  Award,
  GitBranch,
  Cpu,
  Scale,
  QrCode,
  Loader2,
  Copy,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import {
  PLANS,
  ADDONS,
  USAGE_RATES,
  PlanTierKey,
  calculateDecisionTreeEstimate,
  DecisionTreeInputs,
} from "@/lib/pricingData";

declare global {
  interface Window {
    Cashfree?: any;
  }
}

export default function PricingClient() {
  const searchParams = useSearchParams();
  const router = useRouter();

  // Read URL params reactively
  const urlTrack = searchParams?.get("track") || searchParams?.get("model");
  const isUrlBespoke =
    urlTrack === "bespoke" || urlTrack === "enterprise" || urlTrack === "custom";

  const [commercialTrack, setCommercialTrack] = useState<"saas" | "bespoke">(
    isUrlBespoke ? "bespoke" : "saas"
  );
  const [currency, setCurrency] = useState<"INR" | "USD" | "AED">("INR");
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">("monthly");
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [activeAddonCategory, setActiveAddonCategory] = useState<
    "all" | "implementation" | "integration" | "customization" | "migration" | "telephony"
  >("all");

  // Mobile responsive plan selection & feature collapse state
  const [mobileSelectedPlan, setMobileSelectedPlan] = useState<PlanTierKey | "all">("all");
  const [expandedFeatures, setExpandedFeatures] = useState<Record<string, boolean>>({});

  // Cashfree / Online Checkout Modal State
  const [checkoutModalOpen, setCheckoutModalOpen] = useState(false);
  const [selectedCheckoutPlan, setSelectedCheckoutPlan] = useState<PlanTierKey>("growth");
  const [checkoutTab, setCheckoutTab] = useState<"gateway" | "upi">("gateway");
  const [copiedUpi, setCopiedUpi] = useState(false);
  const [showBankDetails, setShowBankDetails] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientGstin, setClientGstin] = useState("");

  // Decision Tree Questionnaire State
  const [dtTeamSize, setDtTeamSize] = useState<DecisionTreeInputs["teamSize"]>("3-5");
  const [dtChannels, setDtChannels] = useState<Array<"whatsapp" | "sms" | "email" | "autodialer">>([
    "whatsapp",
    "email",
  ]);
  const [dtLeadVolume, setDtLeadVolume] = useState<DecisionTreeInputs["monthlyLeadVolume"]>("500-2500");
  const [dtIntegrations, setDtIntegrations] = useState<
    Array<"standard" | "payment_gateway" | "erp_tally" | "custom_api">
  >(["standard"]);
  const [dtMigration, setDtMigration] = useState<DecisionTreeInputs["migrationNeeds"]>("none");
  const [showDecisionTreeModal, setShowDecisionTreeModal] = useState(false);

  // Sync state if URL searchParams changes
  useEffect(() => {
    if (isUrlBespoke) {
      setCommercialTrack("bespoke");
    } else if (urlTrack === "saas") {
      setCommercialTrack("saas");
    }
  }, [urlTrack, isUrlBespoke]);

  // Currency auto-detect
  useEffect(() => {
    if (typeof window !== "undefined") {
      const urlCurr = searchParams?.get("currency")?.toUpperCase();
      if (urlCurr === "AED" || urlCurr === "USD" || urlCurr === "INR") {
        setCurrency(urlCurr as any);
        return;
      }
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
        if (
          tz.includes("Dubai") ||
          tz.includes("Muscat") ||
          tz.includes("Riyadh") ||
          tz.includes("Qatar") ||
          tz.includes("Bahrain") ||
          tz.includes("Kuwait")
        ) {
          setCurrency("AED");
        } else if (
          tz.includes("America") ||
          tz.includes("Europe") ||
          tz.includes("London") ||
          tz.includes("Chicago") ||
          tz.includes("New_York") ||
          tz.includes("Los_Angeles") ||
          tz.includes("Toronto") ||
          tz.includes("Sydney") ||
          tz.includes("Singapore")
        ) {
          setCurrency("USD");
        }
      } catch (e) {}
    }
  }, [searchParams]);

  const activeTrack: "saas" | "bespoke" = isUrlBespoke ? "bespoke" : commercialTrack;

  const handleSwitchTrack = (track: "saas" | "bespoke") => {
    setCommercialTrack(track);
    const newParams = new URLSearchParams(searchParams?.toString() || "");
    newParams.set("track", track);
    router.push(`/pricing?${newParams.toString()}`, { scroll: false });
  };

  const isINR = currency === "INR";
  const isAED = currency === "AED";
  const currencySymbol = isINR ? "₹" : isAED ? "AED " : "$";

  // Calculate live decision tree recommendation
  const dtResult = calculateDecisionTreeEstimate({
    teamSize: dtTeamSize,
    channels: dtChannels,
    monthlyLeadVolume: dtLeadVolume,
    integrations: dtIntegrations,
    migrationNeeds: dtMigration,
    currency,
  });

  const toggleChannel = (ch: "whatsapp" | "sms" | "email" | "autodialer") => {
    setDtChannels((prev) =>
      prev.includes(ch) ? prev.filter((item) => item !== ch) : [...prev, ch]
    );
  };

  const toggleIntegration = (ig: "standard" | "payment_gateway" | "erp_tally" | "custom_api") => {
    setDtIntegrations((prev) =>
      prev.includes(ig) ? prev.filter((item) => item !== ig) : [...prev, ig]
    );
  };

  const visiblePlans: PlanTierKey[] = ["starter", "growth", "professional", "business", "enterprise"];

  const formatPrice = (amount: number) => {
    if (billingCycle === "annual") {
      const discounted = Math.round(amount * 0.85);
      return `${currencySymbol}${discounted.toLocaleString()}`;
    }
    return `${currencySymbol}${amount.toLocaleString()}`;
  };

  const addonsList = Object.values(ADDONS).filter((addon) =>
    activeAddonCategory === "all" ? true : addon.category === activeAddonCategory
  );

  // Trigger Online Checkout via Cashfree PG or Direct UPI
  const handleLaunchOnlineCheckout = async (planKey: PlanTierKey) => {
    setSelectedCheckoutPlan(planKey);
    setCheckoutModalOpen(true);
    setCheckoutError("");
    setCheckoutTab("gateway");
    setCopiedUpi(false);
  };

  const handleCopyUpi = () => {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText("johnsondev02@ybl");
      setCopiedUpi(true);
      setTimeout(() => setCopiedUpi(false), 3000);
    }
  };

  // Helper to ensure Cashfree JS SDK v3 is available
  const ensureCashfreeSdk = async (): Promise<any> => {
    if (typeof window === "undefined") return null;
    if ((window as any).Cashfree) return (window as any).Cashfree;

    return new Promise((resolve, reject) => {
      const existing = document.querySelector('script[src*="cashfree.com/js/v3/cashfree.js"]') as HTMLScriptElement;
      if (existing) {
        if ((window as any).Cashfree) return resolve((window as any).Cashfree);
        existing.addEventListener("load", () => resolve((window as any).Cashfree));
        existing.addEventListener("error", () => reject(new Error("Failed to load Cashfree script.")));
        return;
      }

      const script = document.createElement("script");
      script.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
      script.async = true;
      script.onload = () => resolve((window as any).Cashfree);
      script.onerror = () => reject(new Error("Failed to load Cashfree Payment SDK."));
      document.head.appendChild(script);
    });
  };

  const handleProcessCashfreePayment = async () => {
    setCheckoutLoading(true);
    setCheckoutError("");

    try {
      const res = await fetch("/api/crm/subscription/cashfree/create-order", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          planTier: selectedCheckoutPlan,
          billingCycle,
          customerName: clientName.trim() || undefined,
          customerPhone: clientPhone.trim() || undefined,
          customerEmail: clientEmail.trim() || undefined,
          redirectPath: "/crm",
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        if (data.requiresConfig) {
          setCheckoutTab("upi");
          setCheckoutError(
            "Online card gateway synchronization is in progress. Please complete instant 0-fee activation via Direct UPI below or confirm on WhatsApp."
          );
          setCheckoutLoading(false);
          return;
        }
        throw new Error(data.error || "Failed to create Cashfree payment order");
      }

      if (!data.paymentSessionId) {
        throw new Error("Payment gateway did not return a valid session ID.");
      }

      // Ensure SDK is ready
      const CashfreeConstructor = await ensureCashfreeSdk();
      if (!CashfreeConstructor) {
        throw new Error("Cashfree SDK failed to initialize. Please check your internet connection.");
      }

      const cashfree = CashfreeConstructor({
        mode: data.environment === "production" ? "production" : "sandbox",
      });

      cashfree.checkout({
        paymentSessionId: data.paymentSessionId,
        redirectTarget: "_self",
      });
    } catch (err: any) {
      console.error("[Cashfree Checkout Error]:", err);
      setCheckoutError(err.message || "Payment initiation failed. Please try UPI or WhatsApp.");
      setCheckoutLoading(false);
    }
  };

  const faqs = [
    {
      q: "What is the difference between Model A (CRM SaaS) and Model B (Bespoke Software)?",
      a: "Model A is our managed multi-tenant and dedicated cloud CRM SaaS platform. You subscribe to a monthly software retainer starting at ₹2,499/mo with zero per-seat taxes and receive instant 72-hour turnkey deployment. Model B is bespoke software engineering for clients with proprietary business logic, custom broker integrations, trading platforms, or custom ERPs. In Model B, you own 100% of the IP and source code, delivered through our Core Architecture leadership and vetted engineering delivery network.",
    },
    {
      q: "Why is Phase 0 Discovery & Architecture mandatory for bespoke projects?",
      a: "Custom software failure rates are overwhelmingly caused by vague requirements and misaligned expectations. Phase 0 is a 1-to-2 week paid professional engagement (₹25k–₹75k) where our Solution Architect produces your PRD, system architecture diagram, database schema, and fixed-bid milestone SOW. It protects both parties from scope creep. Crucially, 100% of the Phase 0 fee is credited toward your project implementation upon commissioning.",
    },
    {
      q: "How does your CRM SaaS pricing differ from per-seat platforms like Salesforce or Zoho?",
      a: "Traditional CRM platforms charge $50 to $150 per rep every month, penalizing you as you hire. Johnson Softwares includes generous user pools (up to 2 in Starter, 5 in Growth, 10 in Professional, 20 in Business, and unlimited in Enterprise) with zero incremental per-seat SaaS taxes. You save ₹2.5L+ annually on staff licenses alone.",
    },
    {
      q: "What payment modes are supported for instant online checkout?",
      a: "We support over 48 payment modes powered by Cashfree and Razorpay, including UPI (Google Pay, PhonePe, Paytm, BHIM), NetBanking across 50+ banks, Credit & Debit Cards (Visa, MasterCard, RuPay, Amex), Corporate Wire/NEFT/IMPS, and EMI/PayLater options.",
    },
    {
      q: "Why are WhatsApp, SMS, and voice calls billed separately from the base subscription?",
      a: "We believe in complete billing transparency without hidden markups. Your software retainer covers the CRM engine, hosting, webhooks, and automation bots. Communication usage (Meta WhatsApp 24h conversation windows, Twilio/carrier SMS segments, and outbound calling minutes) is billed at actual wholesale pass-through rates or via direct cards on file. This prevents heavy dispatchers from subsidizing light users.",
    },
    {
      q: "Do we own the source code and intellectual property for bespoke software projects?",
      a: "Yes! For all Model B bespoke software engagements, 100% of the source code, database architecture, CI/CD pipelines, and intellectual property are exclusively assigned to your company upon final milestone payment. For Model A SaaS subscriptions, you own 100% of your business data with full SQLite/CSV exports anytime.",
    },
    {
      q: "What are your downgrade rules? Will our historical leads or data be deleted?",
      a: "No! We have a strict Non-Destructive Downgrade Policy. If you downgrade (for example from Business to Growth), zero leads, messages, documents, or audit records are ever deleted. If you have more active users than allowed under the new plan, accounts are placed in a 'seat pending selection' status so your administrator can choose which reps stay active without losing any historical record.",
    },
    {
      q: "How does the delivery network model work? Who builds our software?",
      a: "We operate with a permanent core architecture and delivery team (Solution Architect, Technical Lead, Product/Client Manager), supported by a vetted engineering network of senior developers, UI/UX designers, and DevOps engineers that we scale based on project requirements. Clients always interface with our senior leadership, guaranteeing enterprise quality and operational certainty.",
    },
  ];

  return (
    <div className="w-full bg-[#F8F8F6] dark:bg-slate-950 text-[#111318] dark:text-slate-100 overflow-x-hidden">
      {/* Cashfree PG Script */}
      <Script
        src="https://sdk.cashfree.com/js/v3/cashfree.js"
        strategy="afterInteractive"
      />

      {/* Architectural Hero Section */}
      <section className="relative min-h-[60vh] sm:min-h-[68vh] w-full flex items-center justify-start px-6 sm:px-12 lg:px-20 py-16 sm:py-20 overflow-hidden border-b border-[#111318]/10 dark:border-slate-800 bg-slate-900">
        <div className="absolute inset-0 z-0">
          <Image
            src="/images/storytelling/office/escalator-architecture.webp"
            alt="Johnson Softwares Commercial Architecture Escalator"
            fill
            priority
            sizes="100vw"
            quality={85}
            className="object-cover object-center"
          />
        </div>

        <div className="absolute inset-y-0 left-0 w-full sm:w-[75%] lg:w-[60%] bg-gradient-to-r from-white/95 via-white/85 dark:from-slate-950/95 dark:via-slate-950/85 to-transparent pointer-events-none z-[1]" />

        {/* Hero Content */}
        <div className="relative z-10 max-w-3xl text-left">
          <div className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full bg-white/95 dark:bg-slate-900/90 border border-[#111318]/10 dark:border-slate-700 shadow-xs text-xs font-mono text-[#111318] dark:text-slate-200 mb-5">
            <span className="w-2 h-2 rounded-full bg-[#2563EB] animate-pulse" />
            <span className="tracking-wide font-medium">COMMERCIAL ARCHITECTURE // DUAL-TRACK ENGAGEMENT</span>
          </div>

          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold text-[#111318] dark:text-white tracking-tight leading-[1.08] mb-5">
            PREDICTABLE SOFTWARE PRICING.
            <br />
            <span className="text-[#2563EB]">ENGINEERED FOR CERTAINTY.</span>
          </h1>

          <p className="text-sm sm:text-base text-[#374151] dark:text-slate-300 leading-relaxed mb-6 max-w-2xl font-normal">
            Dual commercial model: Choose <strong className="text-slate-900 dark:text-white">Model A</strong> for turnkey CRM SaaS with zero per-seat taxes, or <strong className="text-slate-900 dark:text-white">Model B</strong> for bespoke software engineering directed by our core architecture team.
          </p>

          {/* Currency Selector & Instant Quote Controls */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="inline-flex items-center bg-white/95 dark:bg-slate-900 border border-[#111318]/10 dark:border-slate-800 rounded-xl p-1 shadow-sm">
              <button
                onClick={() => setCurrency("INR")}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${
                  currency === "INR"
                    ? "bg-[#2563EB] text-white shadow-sm"
                    : "text-[#5E636B] hover:text-[#111318] dark:text-slate-400 dark:hover:text-white"
                }`}
              >
                🇮🇳 INR (₹)
              </button>
              <button
                onClick={() => setCurrency("USD")}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${
                  currency === "USD"
                    ? "bg-[#2563EB] text-white shadow-sm"
                    : "text-[#5E636B] hover:text-[#111318] dark:text-slate-400 dark:hover:text-white"
                }`}
              >
                🇺🇸 USD ($)
              </button>
              <button
                onClick={() => setCurrency("AED")}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${
                  currency === "AED"
                    ? "bg-[#2563EB] text-white shadow-sm"
                    : "text-[#5E636B] hover:text-[#111318] dark:text-slate-400 dark:hover:text-white"
                }`}
              >
                🇦🇪 AED (AED)
              </button>
            </div>

            <div className="inline-flex items-center bg-white/95 dark:bg-slate-900 border border-[#111318]/10 dark:border-slate-800 rounded-xl p-1 shadow-sm">
              <button
                onClick={() => setBillingCycle("monthly")}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${
                  billingCycle === "monthly"
                    ? "bg-[#111318] dark:bg-slate-700 text-white shadow-sm"
                    : "text-[#5E636B] hover:text-[#111318] dark:text-slate-400 dark:hover:text-white"
                }`}
              >
                Monthly Retainer
              </button>
              <button
                onClick={() => setBillingCycle("annual")}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all flex items-center gap-1.5 ${
                  billingCycle === "annual"
                    ? "bg-emerald-600 text-white shadow-sm"
                    : "text-[#5E636B] hover:text-[#111318] dark:text-slate-400 dark:hover:text-white"
                }`}
              >
                Annual (Save 15%)
                <span className="px-1.5 py-0.5 text-[9px] bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 rounded font-bold">
                  -15%
                </span>
              </button>
            </div>

            <button
              onClick={() => setShowDecisionTreeModal(true)}
              className="inline-flex items-center gap-2 px-3.5 py-2 text-xs font-bold rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-300 hover:bg-amber-500/20 transition-colors shadow-xs"
            >
              <Calculator className="w-4 h-4 text-amber-600 dark:text-amber-400" />
              <span>Instant Quote Decision Tree</span>
            </button>
          </div>
        </div>
      </section>

      {/* STICKY IN-PAGE MASTER TRACK SWITCHER (ALWAYS VISIBLE ABOVE CONTENT) */}
      <section className="sticky top-16 z-30 bg-[#F8F8F6]/95 dark:bg-slate-950/95 backdrop-blur-md border-b border-slate-200 dark:border-slate-800 py-3 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="inline-flex items-center p-1 rounded-2xl bg-slate-200 dark:bg-slate-900 border border-slate-300 dark:border-slate-800 w-full sm:w-auto shadow-inner">
            <button
              onClick={() => handleSwitchTrack("saas")}
              className={`flex-1 sm:flex-initial flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all ${
                activeTrack === "saas"
                  ? "bg-[#2563EB] text-white shadow-md shadow-blue-500/20"
                  : "text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
              }`}
            >
              <Database className="w-4 h-4" />
              <span>Model A: CRM SaaS Platform</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/20 hidden md:inline">
                Flat Tiers
              </span>
            </button>

            <button
              onClick={() => handleSwitchTrack("bespoke")}
              className={`flex-1 sm:flex-initial flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all ${
                activeTrack === "bespoke"
                  ? "bg-amber-500 text-slate-950 shadow-md shadow-amber-500/20 font-black"
                  : "text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
              }`}
            >
              <Workflow className="w-4 h-4" />
              <span>Model B: Bespoke Enterprise Projects</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-400/30 text-amber-950 font-bold hidden md:inline">
                Phase 0 Discovery
              </span>
            </button>
          </div>

          <div className="flex items-center gap-3 text-xs">
            <div className="flex items-center gap-1.5 font-mono text-[11px] text-slate-600 dark:text-slate-400">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
              <span>{activeTrack === "saas" ? "Non-Destructive Downgrade SLA" : "100% IP Transfer & SOW Certainty"}</span>
            </div>
          </div>
        </div>
      </section>

      {/* TRACK A: CRM SAAS PLATFORM CONTENT */}
      {activeTrack === "saas" && (
        <>
          <section className="pt-8 pb-4 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-slate-200 dark:border-slate-800 pb-5">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 text-xs font-bold uppercase tracking-wider mb-2">
                  <Database className="w-3.5 h-3.5" />
                  Model A — CRM SaaS Platform Subscriptions
                </div>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white">
                  Flat Subscription Tiers with Zero Per-Seat Taxes
                </h2>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Every tier includes pooled user logins, multi-industry pipelines, dynamic schemas, and managed cloud containers.
                </p>
              </div>

              <button
                onClick={() => handleSwitchTrack("bespoke")}
                className="inline-flex items-center gap-2 text-xs font-bold text-amber-700 dark:text-amber-300 bg-amber-500/10 border border-amber-500/30 px-4 py-2.5 rounded-xl hover:bg-amber-500/20 transition-all shrink-0 self-start md:self-auto shadow-xs"
              >
                <span>Need custom broker software or ERP? Switch to Model B</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </section>

          {/* 5-Tier Pricing Grid */}
          <section className="py-6 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
            {/* Quick Tier Filter Switcher (Mobile & Tablet) */}
            <div className="mb-6 flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-1.5 overflow-x-auto pb-1 max-w-full no-scrollbar">
                <button
                  type="button"
                  onClick={() => setMobileSelectedPlan("all")}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-bold shrink-0 transition-all cursor-pointer ${
                    mobileSelectedPlan === "all"
                      ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900 shadow-sm"
                      : "bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-800 hover:border-slate-300"
                  }`}
                >
                  All 5 Plans
                </button>
                {visiblePlans.map((k) => {
                  const p = PLANS[k];
                  const isSelected = mobileSelectedPlan === k;
                  return (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setMobileSelectedPlan(k)}
                      className={`px-3.5 py-1.5 rounded-full text-xs font-bold shrink-0 flex items-center gap-1 transition-all cursor-pointer ${
                        isSelected
                          ? "bg-primary text-white shadow-sm ring-2 ring-primary/20"
                          : "bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-800 hover:border-slate-300"
                      }`}
                    >
                      <span>{p.name}</span>
                      {p.popular && <span className="text-[10px]">⭐</span>}
                    </button>
                  );
                })}
              </div>

              {mobileSelectedPlan !== "all" && (
                <button
                  type="button"
                  onClick={() => setMobileSelectedPlan("all")}
                  className="text-xs font-bold text-primary hover:underline cursor-pointer"
                >
                  Show all 5 tiers →
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
              {visiblePlans
                .filter((k) => mobileSelectedPlan === "all" || k === mobileSelectedPlan)
                .map((key) => {
                const plan = PLANS[key];
                const isPopular = plan.popular;
                const monthlyPrice = plan.pricing[currency].monthly;
                const setupPrice = plan.pricing[currency].setup;
                const isExpanded = !!expandedFeatures[key];
                const featuresToDisplay = isExpanded ? plan.displayFeatures : plan.displayFeatures.slice(0, 5);
                const hasMoreFeatures = plan.displayFeatures.length > 5;

                return (
                  <div
                    key={plan.id}
                    className={`relative rounded-2xl flex flex-col justify-between transition-all duration-300 ${
                      isPopular
                        ? "bg-white dark:bg-slate-900 border-2 border-primary shadow-xl shadow-primary/10 ring-1 ring-primary/20 xl:-translate-y-2"
                        : "bg-white/80 dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 shadow-sm"
                    }`}
                  >
                    {isPopular && (
                      <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-primary to-amber-500 text-white text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-full shadow-md">
                        {plan.badge || "MOST POPULAR"}
                      </div>
                    )}

                    <div className="p-5">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                          {plan.name}
                        </h3>
                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                          {plan.seatsBadge}
                        </span>
                      </div>

                      <p className="text-xs text-slate-500 dark:text-slate-400 min-h-[36px] mb-4">
                        {plan.tagline}
                      </p>

                      <div className="mb-4 pb-4 border-b border-slate-100 dark:border-slate-800">
                        <div className="flex items-baseline gap-1">
                          {key === "enterprise" ? (
                            <>
                              <span className="text-xs font-semibold text-slate-500">Starting from</span>
                              <span className="text-2xl font-black text-slate-900 dark:text-white">
                                {formatPrice(monthlyPrice)}
                              </span>
                            </>
                          ) : (
                            <span className="text-3xl font-black text-slate-900 dark:text-white">
                              {formatPrice(monthlyPrice)}
                            </span>
                          )}
                          <span className="text-xs text-slate-500">/ month</span>
                        </div>
                        <div className="mt-1 flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400">
                          <span>VIP Managed Setup:</span>
                          <span className="font-semibold text-slate-700 dark:text-slate-300">
                            {currencySymbol}
                            {setupPrice.toLocaleString()}
                            {key === "business" || key === "enterprise" ? "+" : ""}
                          </span>
                        </div>
                        <div className="mt-0.5 text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1">
                          <Check className="w-3 h-3 text-emerald-500 shrink-0" />
                          <span>Self-serve trial: Free / ₹0 setup</span>
                        </div>
                      </div>

                      <div className="space-y-2 mb-6 text-xs">
                        <p className="font-bold text-[11px] uppercase tracking-wider text-slate-400 dark:text-slate-500">
                          Included Capabilities:
                        </p>
                        {featuresToDisplay.map((feat, idx) => (
                          <div key={idx} className="flex items-start gap-2 text-slate-700 dark:text-slate-300">
                            <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                            <span>{feat}</span>
                          </div>
                        ))}
                        {hasMoreFeatures && (
                          <button
                            type="button"
                            onClick={() =>
                              setExpandedFeatures((prev) => ({
                                ...prev,
                                [key]: !prev[key],
                              }))
                            }
                            className="text-[11px] font-bold text-primary hover:underline flex items-center gap-1 pt-1.5 cursor-pointer"
                          >
                            {isExpanded ? (
                              <>
                                <span>Show core capabilities only</span>
                                <ChevronUp className="w-3 h-3" />
                              </>
                            ) : (
                              <>
                                <span>+ View all {plan.displayFeatures.length} capabilities</span>
                                <ChevronDown className="w-3 h-3" />
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="p-5 pt-0 space-y-2">
                      {key === "enterprise" ? (
                        <a
                          href="https://wa.me/919880893416?text=Hi%20Johnson%20Softwares,%20we%20would%20like%20to%20talk%20to%20a%20Solution%20Architect%20about%20the%20Enterprise%20Plan."
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full py-2.5 px-4 rounded-xl text-xs font-bold text-center flex items-center justify-center gap-2 bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 hover:opacity-95 shadow-md shadow-amber-500/20 transition-all cursor-pointer"
                        >
                          Talk to a Solution Architect →
                        </a>
                      ) : key === "business" ? (
                        <>
                          <a
                            href="https://wa.me/919880893416?text=Hi%20Johnson%20Softwares,%20we%20would%20like%20to%20book%20a%20live%20demo%20for%20the%20Business%20CRM%20Plan."
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full py-2.5 px-4 rounded-xl text-xs font-bold text-center flex items-center justify-center gap-2 bg-slate-900 text-white dark:bg-white dark:text-slate-900 hover:opacity-90 shadow-md transition-all cursor-pointer"
                          >
                            Book a Demo
                            <ArrowRight className="w-3.5 h-3.5" />
                          </a>
                          <button
                            onClick={() => handleLaunchOnlineCheckout(key)}
                            className="w-full py-2 px-3 rounded-lg border border-slate-200 dark:border-slate-800 hover:border-primary/50 text-[11px] font-semibold text-slate-600 dark:text-slate-400 hover:text-primary dark:hover:text-primary transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                          >
                            <CreditCard className="w-3 h-3 text-emerald-500" />
                            <span>Pay Online (Instant Setup)</span>
                          </button>
                        </>
                      ) : (
                        <>
                          <Link
                            href={`/register?plan=${key}`}
                            className={`w-full py-2.5 px-4 rounded-xl text-xs font-bold text-center flex items-center justify-center gap-2 transition-all ${
                              isPopular
                                ? "bg-primary text-white hover:bg-primary/90 shadow-md shadow-primary/20"
                                : "bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white hover:bg-slate-200 dark:hover:bg-slate-700"
                            }`}
                          >
                            Start 7-Day Trial
                            <ArrowRight className="w-3.5 h-3.5" />
                          </Link>
                          <button
                            onClick={() => handleLaunchOnlineCheckout(key)}
                            className="w-full py-2 px-3 rounded-lg border border-slate-200 dark:border-slate-800 hover:border-primary/50 text-[11px] font-semibold text-slate-600 dark:text-slate-400 hover:text-primary dark:hover:text-primary transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                          >
                            <CreditCard className="w-3 h-3 text-emerald-500" />
                            <span>Pay Online (UPI / Card / 48 Modes)</span>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Commercial Transparency & Boundary Banner */}
            <div className="mt-8 p-5 sm:p-6 rounded-2xl bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800 shadow-sm text-xs text-slate-600 dark:text-slate-400 space-y-3">
              <div className="flex items-center gap-2 font-bold text-slate-900 dark:text-white text-sm">
                <ShieldCheck className="w-4 h-4 text-emerald-500 shrink-0" />
                <span>Commercial Transparency & Delivery Boundary Policy</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-1">
                <div className="space-y-1">
                  <span className="font-bold text-slate-900 dark:text-slate-200 block text-xs">
                    Meta & Wholesale Carrier Billing
                  </span>
                  <p className="leading-relaxed">
                    Official WhatsApp Cloud API integration is included in Growth, Professional, Business, and Enterprise plans. Third-party messaging charges (Meta 24h conversation windows, direct SMS segments, telephony minutes) are billed separately at direct wholesale pass-through.
                  </p>
                </div>
                <div className="space-y-1">
                  <span className="font-bold text-slate-900 dark:text-slate-200 block text-xs">
                    AI Voice & Token Consumption
                  </span>
                  <p className="leading-relaxed">
                    AI Voice Calling and intelligent qualification assistants are integrated at the platform layer. Underlying voice telephony gateway charges and AI provider LLM tokens are billed directly based on actual operational usage.
                  </p>
                </div>
                <div className="space-y-1">
                  <span className="font-bold text-slate-900 dark:text-slate-200 block text-xs">
                    Implementation & Custom Scope
                  </span>
                  <p className="leading-relaxed">
                    Subscription includes all features listed in your selected plan. Custom software development, bespoke ERP/legacy system sync, and major custom workflow automations outside standard limits are separately scoped and billed under Model B.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Interactive Sales Decision Tree / Calculator */}
          <section className="py-10 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
            <div className="rounded-3xl bg-gradient-to-br from-slate-900 via-slate-950 to-primary/20 border border-slate-800 p-8 sm:p-12 shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-96 h-96 bg-primary/10 blur-[100px] rounded-full pointer-events-none" />

              <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8 mb-8 pb-8 border-b border-slate-800">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-bold tracking-wider uppercase mb-3">
                    <Calculator className="w-3.5 h-3.5" />
                    Live Decision Tree Calculator
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-extrabold text-white">
                    Calculate an Instant On-Call Commercial Quote
                  </h2>
                  <p className="mt-2 text-sm text-slate-400 max-w-2xl">
                    Select team parameters below to determine the exact recommended plan, implementation fee, software retainer, and wholesale carrier pass-through range.
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-400 font-medium">Currency:</span>
                  <div className="inline-flex rounded-lg border border-slate-800 bg-slate-900 p-1 text-xs font-bold">
                    {(["INR", "USD", "AED"] as const).map((c) => (
                      <button
                        key={c}
                        onClick={() => setCurrency(c)}
                        className={`px-2.5 py-1 rounded ${
                          currency === c ? "bg-primary text-white" : "text-slate-400 hover:text-white"
                        }`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Interactive Form Controls */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                <div className="bg-slate-900/90 border border-slate-800 p-5 rounded-2xl flex flex-col justify-between">
                  <div>
                    <label className="block text-xs font-bold text-slate-200 uppercase tracking-wider mb-2.5">
                      1. Team Size / Sales Reps
                    </label>
                    <div className="relative">
                      <select
                        value={dtTeamSize}
                        onChange={(e) => setDtTeamSize(e.target.value as any)}
                        className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-3.5 py-2.5 text-xs font-medium focus:ring-2 focus:ring-primary outline-none cursor-pointer appearance-none pr-8"
                      >
                        <option value="1-2">1 to 2 Users (Starter)</option>
                        <option value="3-5">3 to 5 Users (Growth)</option>
                        <option value="6-10">6 to 10 Users (Professional)</option>
                        <option value="11-20">11 to 20 Users (Business)</option>
                        <option value="20+">20+ Users (Enterprise Multi-Branch)</option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-slate-400">
                        <ChevronDown className="w-3.5 h-3.5" />
                      </div>
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-3 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
                    Zero per-seat SaaS tax across all tiers.
                  </p>
                </div>

                <div className="bg-slate-900/90 border border-slate-800 p-5 rounded-2xl flex flex-col justify-between">
                  <div>
                    <label className="block text-xs font-bold text-slate-200 uppercase tracking-wider mb-2.5">
                      2. Channels Needed
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { id: "whatsapp", label: "Meta WhatsApp" },
                        { id: "email", label: "Email Auto" },
                        { id: "sms", label: "Direct SMS" },
                        { id: "autodialer", label: "Auto-Dialer" },
                      ].map((ch) => (
                        <button
                          key={ch.id}
                          type="button"
                          onClick={() => toggleChannel(ch.id as any)}
                          className={`py-2 px-2.5 rounded-xl border text-left flex items-center gap-2 transition-all cursor-pointer ${
                            dtChannels.includes(ch.id as any)
                              ? "bg-primary/20 border-primary text-white shadow-xs"
                              : "bg-slate-800/70 border-slate-700/80 text-slate-300 hover:text-white hover:bg-slate-800"
                          }`}
                        >
                          <CheckCircle2
                            className={`w-3.5 h-3.5 shrink-0 ${
                              dtChannels.includes(ch.id as any) ? "text-primary" : "opacity-30"
                            }`}
                          />
                          <span className="text-xs font-medium truncate">{ch.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-3">Select communication pipelines.</p>
                </div>

                <div className="bg-slate-900/90 border border-slate-800 p-5 rounded-2xl flex flex-col justify-between">
                  <div>
                    <label className="block text-xs font-bold text-slate-200 uppercase tracking-wider mb-2.5">
                      3. Monthly Lead Volume
                    </label>
                    <div className="relative">
                      <select
                        value={dtLeadVolume}
                        onChange={(e) => setDtLeadVolume(e.target.value as any)}
                        className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-3.5 py-2.5 text-xs font-medium focus:ring-2 focus:ring-primary outline-none cursor-pointer appearance-none pr-8"
                      >
                        <option value="<500">Under 500 Leads / month</option>
                        <option value="500-2500">500 to 2,500 Leads / month</option>
                        <option value="2500-10000">2,500 to 10,000 Leads / month</option>
                        <option value="10000+">10,000+ Leads / month</option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-slate-400">
                        <ChevronDown className="w-3.5 h-3.5" />
                      </div>
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-3">Determines wholesale carrier usage.</p>
                </div>

                <div className="bg-slate-900/90 border border-slate-800 p-5 rounded-2xl flex flex-col justify-between">
                  <div>
                    <label className="block text-xs font-bold text-slate-200 uppercase tracking-wider mb-2.5">
                      4. Add-ons & Migration
                    </label>
                    <div className="space-y-2">
                      <button
                        type="button"
                        onClick={() => toggleIntegration("payment_gateway")}
                        className={`w-full py-2 px-3 rounded-xl border text-left text-xs font-medium flex items-center justify-between transition-all cursor-pointer ${
                          dtIntegrations.includes("payment_gateway")
                            ? "bg-primary/20 border-primary text-white shadow-xs"
                            : "bg-slate-800/70 border-slate-700/80 text-slate-300 hover:text-white hover:bg-slate-800"
                        }`}
                      >
                        <span className="truncate">Payment Gateway (Cashfree/Razorpay)</span>
                        <Check
                          className={`w-3.5 h-3.5 shrink-0 ml-1.5 ${
                            dtIntegrations.includes("payment_gateway") ? "text-primary" : "opacity-0"
                          }`}
                        />
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleIntegration("erp_tally")}
                        className={`w-full py-2 px-3 rounded-xl border text-left text-xs font-medium flex items-center justify-between transition-all cursor-pointer ${
                          dtIntegrations.includes("erp_tally")
                            ? "bg-primary/20 border-primary text-white shadow-xs"
                            : "bg-slate-800/70 border-slate-700/80 text-slate-300 hover:text-white hover:bg-slate-800"
                        }`}
                      >
                        <span className="truncate">Tally / ERP Sync</span>
                        <Check
                          className={`w-3.5 h-3.5 shrink-0 ml-1.5 ${
                            dtIntegrations.includes("erp_tally") ? "text-primary" : "opacity-0"
                          }`}
                        />
                      </button>
                      <button
                        type="button"
                        onClick={() => setDtMigration(dtMigration === "none" ? "csv_standard" : "none")}
                        className={`w-full py-2 px-3 rounded-xl border text-left text-xs font-medium flex items-center justify-between transition-all cursor-pointer ${
                          dtMigration !== "none"
                            ? "bg-primary/20 border-primary text-white shadow-xs"
                            : "bg-slate-800/70 border-slate-700/80 text-slate-300 hover:text-white hover:bg-slate-800"
                        }`}
                      >
                        <span className="truncate">Legacy CRM Data Migration</span>
                        <Check
                          className={`w-3.5 h-3.5 shrink-0 ml-1.5 ${
                            dtMigration !== "none" ? "text-primary" : "opacity-0"
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-3">Optional turnkey integrations.</p>
                </div>
              </div>

              {/* Dynamic Results Card */}
              <div className="mt-8 bg-slate-900 border border-primary/30 rounded-2xl p-6 shadow-inner">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-center">
                  <div>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-primary">
                      Recommended Tier
                    </span>
                    <div className="text-2xl font-black text-white mt-1">
                      {dtResult.recommendedPlan.name}
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      {dtResult.recommendedPlan.seatsBadge}
                    </div>
                  </div>

                  <div>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                      Monthly Software Retainer
                    </span>
                    <div className="text-2xl font-black text-emerald-400 mt-1">
                      {currencySymbol}
                      {dtResult.monthlySoftwareFee.toLocaleString()}
                      <span className="text-xs font-normal text-slate-400"> / mo</span>
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      Includes hosting & automation
                    </div>
                  </div>

                  <div>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                      Turnkey Implementation
                    </span>
                    <div className="text-2xl font-black text-white mt-1">
                      {currencySymbol}
                      {dtResult.implementationFee.toLocaleString()}
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      One-time onboarding & config
                    </div>
                  </div>

                  <div>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-amber-400">
                      Estimated Carrier Usage
                    </span>
                    <div className="text-base font-bold text-amber-300 mt-1">
                      {currencySymbol}
                      {dtResult.estimatedMonthlyUsage.min.toLocaleString()} - {currencySymbol}
                      {dtResult.estimatedMonthlyUsage.max.toLocaleString()}
                      <span className="text-xs font-normal text-slate-400"> / mo</span>
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5">
                      Wholesale pass-through (Meta & SMS)
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="text-xs text-slate-400 space-y-1">
                    {dtResult.rationales.slice(0, 2).map((r, i) => (
                      <p key={i} className="flex items-center gap-1.5">
                        <Check className="w-3.5 h-3.5 text-primary shrink-0" />
                        <span>{r}</span>
                      </p>
                    ))}
                  </div>

                  <a
                    href={`https://wa.me/919880893416?text=Hi%20Johnson%20Softwares,%20based%20on%20the%20Decision%20Tree,%20our%20recommended%20tier%20is%20${encodeURIComponent(
                      dtResult.recommendedPlan.name
                    )}%20(${currencySymbol}${dtResult.monthlySoftwareFee}/mo%20+%20Setup%20${currencySymbol}${
                      dtResult.implementationFee
                    }).%20Please%20send%20us%20a%20formal%20SOW.`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full sm:w-auto px-6 py-3 rounded-xl bg-gradient-to-r from-primary to-amber-500 text-white text-xs font-bold flex items-center justify-center gap-2 hover:opacity-95 shadow-lg shadow-primary/20 transition-all"
                  >
                    Lock In This Quote On WhatsApp
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Modular Add-Ons Catalog */}
          <section className="py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs font-bold uppercase tracking-wider mb-2">
                <Layers className="w-3.5 h-3.5 text-primary" />
                Independent Add-On Modules
              </div>
              <h2 className="text-3xl font-extrabold text-slate-900 dark:text-white">
                Add-On & Engineering Services Rate Card
              </h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400 max-w-2xl mx-auto">
                Turnkey configurations, third-party integrations, custom workflows, and migrations.
              </p>

              <div className="flex flex-wrap items-center justify-center gap-2 mt-5">
                {[
                  { id: "all", label: "All Modules" },
                  { id: "implementation", label: "Implementation" },
                  { id: "integration", label: "Integrations" },
                  { id: "customization", label: "Custom Workflows" },
                  { id: "migration", label: "Migration" },
                  { id: "telephony", label: "Auto-Dialer" },
                ].map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveAddonCategory(cat.id as any)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                      activeAddonCategory === cat.id
                        ? "bg-primary text-white shadow-sm"
                        : "bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {addonsList.map((addon) => {
                const setupPrice = addon.pricing[currency].setup;
                const monthlyPrice = addon.pricing[currency].monthly;

                return (
                  <div
                    key={addon.id}
                    className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between"
                  >
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                          {addon.category}
                        </span>
                        <span className="text-xs font-bold text-slate-500">
                          {addon.isRecurring ? "Setup + Monthly" : "One-Time Fee"}
                        </span>
                      </div>

                      <h3 className="text-base font-bold text-slate-900 dark:text-white mb-2">
                        {addon.name}
                      </h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed mb-4">
                        {addon.description}
                      </p>
                    </div>

                    <div className="pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
                      <div>
                        <div className="text-lg font-black text-slate-900 dark:text-white">
                          {currencySymbol}
                          {setupPrice.toLocaleString()}
                        </div>
                        {addon.isRecurring && monthlyPrice > 0 && (
                          <div className="text-[11px] text-emerald-500 font-semibold">
                            + {currencySymbol}
                            {monthlyPrice.toLocaleString()} / mo
                          </div>
                        )}
                      </div>

                      <a
                        href={`https://wa.me/919880893416?text=Hi%20Johnson%20Softwares,%20we%20would%20like%20to%20add%20the%20${encodeURIComponent(
                          addon.name
                        )}%20add-on%20to%20our%20account.`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-primary hover:text-white text-slate-700 dark:text-slate-300 text-xs font-bold transition-colors"
                      >
                        Add Module
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Wholesale Carrier Pass-Through Rates */}
          <section className="py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
            <div className="bg-amber-500/5 dark:bg-amber-500/10 border border-amber-500/20 rounded-3xl p-8 sm:p-10">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-8">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/20 text-amber-700 dark:text-amber-400 text-xs font-bold uppercase tracking-wider mb-2">
                    <Zap className="w-3.5 h-3.5" />
                    Zero-Markup Wholesale Usage Model
                  </div>
                  <h3 className="text-2xl font-extrabold text-slate-900 dark:text-white">
                    Why Communication Charges Are Kept Completely Separate
                  </h3>
                  <p className="mt-2 text-xs sm:text-sm text-slate-600 dark:text-slate-400 max-w-2xl">
                    Unlike bundled vendors who markup telecom rates by 300%, Johnson Softwares charges only for software logic and VPS containers. Official Meta WhatsApp and SMS carrier fees are billed at pure wholesale pass-through.
                  </p>
                </div>

                <div className="flex items-center gap-2 bg-white dark:bg-slate-900 border border-amber-500/30 px-4 py-2 rounded-xl text-xs font-bold text-amber-600 dark:text-amber-400 shrink-0">
                  <ShieldCheck className="w-4 h-4 text-emerald-500" />
                  100% Direct Carrier Transparency
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 rounded-2xl">
                  <span className="text-[11px] font-bold uppercase text-slate-400">WhatsApp Service Window</span>
                  <div className="text-xl font-black text-slate-900 dark:text-white mt-1">
                    {isINR ? "₹0.40" : isAED ? "AED 0.03" : "$0.008"}
                  </div>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                    Per user-initiated 24-hour service conversation window (direct Meta wholesale).
                  </p>
                </div>

                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 rounded-2xl">
                  <span className="text-[11px] font-bold uppercase text-slate-400">WhatsApp Marketing Window</span>
                  <div className="text-xl font-black text-slate-900 dark:text-white mt-1">
                    {isINR ? "₹0.88" : isAED ? "AED 0.06" : "$0.016"}
                  </div>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                    Per business-initiated template broadcast conversation.
                  </p>
                </div>

                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 rounded-2xl">
                  <span className="text-[11px] font-bold uppercase text-slate-400">Direct Telecom SMS</span>
                  <div className="text-xl font-black text-slate-900 dark:text-white mt-1">
                    {isINR ? "₹0.18" : isAED ? "AED 0.12" : "$0.035"}
                  </div>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                    Per standard 160-character telecom segment (DLT / Carrier Direct).
                  </p>
                </div>

                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 rounded-2xl">
                  <span className="text-[11px] font-bold uppercase text-slate-400">Auto-Dialer Voice</span>
                  <div className="text-xl font-black text-slate-900 dark:text-white mt-1">
                    {isINR ? "₹0.95" : isAED ? "AED 0.08" : "$0.025"}
                  </div>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                    Per connected outbound telecalling minute on SIP trunk.
                  </p>
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      {/* TRACK B: BESPOKE ENTERPRISE SOFTWARE CONTENT */}
      {activeTrack === "bespoke" && (
        <>
          <section className="pt-8 pb-4 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-slate-200 dark:border-slate-800 pb-5">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-400 text-xs font-bold uppercase tracking-wider mb-2">
                  <Workflow className="w-3.5 h-3.5" />
                  Model B — Bespoke Software & Enterprise Projects
                </div>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white">
                  High-Certainty Engineering Directed by Core Solution Architects
                </h2>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400 max-w-3xl">
                  For enterprises with unique business logic, custom broker/fintech integrations, proprietary algorithmic engines, or legacy ERP modernization. 100% source code ownership.
                </p>
              </div>

              <button
                onClick={() => handleSwitchTrack("saas")}
                className="inline-flex items-center gap-2 text-xs font-bold text-blue-600 dark:text-blue-400 bg-blue-500/10 border border-blue-500/20 px-4 py-2.5 rounded-xl hover:bg-blue-500/20 transition-all shrink-0 self-start md:self-auto shadow-xs"
              >
                <span>Looking for turnkey CRM SaaS subscription? Switch to Model A</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </section>

          {/* Featured Hero Card: Phase 0 Discovery & Architecture */}
          <section className="py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
            <div className="rounded-3xl bg-gradient-to-br from-slate-950 via-slate-900 to-amber-950/40 border-2 border-amber-500/40 p-8 sm:p-12 shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-96 h-96 bg-amber-500/10 blur-[100px] rounded-full pointer-events-none" />

              <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 mb-8 pb-8 border-b border-slate-800">
                <div>
                  <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-300 text-xs font-black tracking-widest uppercase mb-3">
                    <Compass className="w-3.5 h-3.5" />
                    MANDATORY PROFESSIONAL ENGAGEMENT // 1 TO 2 WEEKS
                  </div>
                  <h3 className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                    Phase 0 — Discovery & Solution Architecture
                  </h3>
                  <p className="mt-2 text-sm text-slate-300 max-w-2xl leading-relaxed">
                    Eliminates scope creep, technical debt, and financial surprises before writing a single line of production code. You receive an exhaustive production blueprint ready for implementation.
                  </p>
                </div>

                <div className="bg-slate-900/90 border border-amber-500/30 rounded-2xl p-5 text-right shrink-0">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                    Phase 0 Architecture Fee
                  </span>
                  <div className="text-3xl font-black text-amber-400 mt-1">
                    {isINR ? "₹25,000 – ₹75,000" : isAED ? "AED 1,250 – AED 3,500" : "$350 – $990"}
                  </div>
                  <div className="text-[11px] text-emerald-400 font-semibold mt-1 flex items-center justify-end gap-1">
                    <CheckCircle2 className="w-3 h-3" />
                    <span>100% credited toward implementation</span>
                  </div>
                </div>
              </div>

              {/* 5 Concrete Deliverables */}
              <div className="mb-8">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-4">
                  What You Receive In Phase 0 Delivery:
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="bg-slate-900/80 border border-slate-800 p-4 rounded-xl">
                    <div className="flex items-center gap-2 text-white font-bold text-sm mb-1.5">
                      <FileText className="w-4 h-4 text-amber-400 shrink-0" />
                      <span>1. Comprehensive PRD & User Stories</span>
                    </div>
                    <p className="text-xs text-slate-400 leading-relaxed">
                      Every workflow, role permission, edge case, and acceptance criterion documented with zero ambiguity.
                    </p>
                  </div>

                  <div className="bg-slate-900/80 border border-slate-800 p-4 rounded-xl">
                    <div className="flex items-center gap-2 text-white font-bold text-sm mb-1.5">
                      <Cpu className="w-4 h-4 text-amber-400 shrink-0" />
                      <span>2. Architecture & Cloud Topology</span>
                    </div>
                    <p className="text-xs text-slate-400 leading-relaxed">
                      Detailed component diagrams, container definitions, microservices boundaries, and multi-region failover.
                    </p>
                  </div>

                  <div className="bg-slate-900/80 border border-slate-800 p-4 rounded-xl">
                    <div className="flex items-center gap-2 text-white font-bold text-sm mb-1.5">
                      <Database className="w-4 h-4 text-amber-400 shrink-0" />
                      <span>3. Relational DB Schema & ERD</span>
                    </div>
                    <p className="text-xs text-slate-400 leading-relaxed">
                      Full SQL/PostgreSQL entity schemas, indexing strategies, foreign key constraints, and audit logging tables.
                    </p>
                  </div>

                  <div className="bg-slate-900/80 border border-slate-800 p-4 rounded-xl">
                    <div className="flex items-center gap-2 text-white font-bold text-sm mb-1.5">
                      <GitBranch className="w-4 h-4 text-amber-400 shrink-0" />
                      <span>4. API Contract & Integration Specs</span>
                    </div>
                    <p className="text-xs text-slate-400 leading-relaxed">
                      REST/WebSocket endpoint schemas for Payment Gateways, Meta WhatsApp API, ERPs, and Trading engines.
                    </p>
                  </div>

                  <div className="bg-slate-900/80 border border-slate-800 p-4 rounded-xl">
                    <div className="flex items-center gap-2 text-white font-bold text-sm mb-1.5">
                      <CheckCircle2 className="w-4 h-4 text-amber-400 shrink-0" />
                      <span>5. Fixed-Bid Milestone Delivery SOW</span>
                    </div>
                    <p className="text-xs text-slate-400 leading-relaxed">
                      Deterministic sprint roadmap, milestone payment gates, team composition, and guaranteed delivery schedule.
                    </p>
                  </div>

                  <div className="bg-amber-500/10 border border-amber-500/30 p-4 rounded-xl flex flex-col justify-between">
                    <div>
                      <div className="flex items-center gap-2 text-amber-300 font-bold text-sm mb-1.5">
                        <Award className="w-4 h-4 text-amber-400 shrink-0" />
                        <span>Investment Credit Guarantee</span>
                      </div>
                      <p className="text-xs text-slate-300 leading-relaxed">
                        When you commission Johnson Softwares to build the system, 100% of your Phase 0 invoice is deducted from Milestone 1.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Phase 0 CTA Row */}
              <div className="pt-6 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-xs text-slate-400 flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span>Led personally by our Principal Solution Architect. Turnaround in 7–10 business days.</span>
                </div>

                <a
                  href={`https://wa.me/919880893416?text=Hi%20Johnson%20Softwares,%20we%20would%20like%20to%20commission%20a%20Phase%200%20Discovery%20%26%20Architecture%20Blueprint%20for%20our%20enterprise%20project.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto px-6 py-3.5 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 font-black text-xs flex items-center justify-center gap-2 hover:opacity-95 shadow-lg shadow-amber-500/20 transition-all"
                >
                  Commission Phase 0 Discovery
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </section>

          {/* Bespoke Project Commercial Tiers */}
          <section className="py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h3 className="text-3xl font-extrabold text-slate-900 dark:text-white">
                Bespoke Implementation Framework
              </h3>
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400 max-w-2xl mx-auto">
                After Phase 0 blueprinting, projects are delivered through fixed-bid milestone gates by our core leadership and vetted engineering delivery network.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Bespoke Tier 1: Custom Implementation */}
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400">
                      Mid-Scale Systems
                    </span>
                    <span className="text-xs font-bold text-slate-500">4 to 8 Weeks</span>
                  </div>

                  <h4 className="text-xl font-bold text-slate-900 dark:text-white mb-1">
                    Custom Implementation
                  </h4>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-4 min-h-[36px]">
                    Tailored business management, bespoke CRM modules, client billing portals, and multi-API orchestrations.
                  </p>

                  <div className="pb-4 mb-4 border-b border-slate-100 dark:border-slate-800">
                    <span className="text-xs text-slate-400 font-semibold uppercase">Project Investment</span>
                    <div className="text-2xl font-black text-slate-900 dark:text-white mt-1">
                      {isINR ? "₹2,00,000 – ₹10,00,000+" : isAED ? "AED 9,000 – AED 45,000+" : "$2,500 – $12,000+"}
                    </div>
                    <span className="text-[11px] text-slate-500 dark:text-slate-400">Fixed-bid milestone payments</span>
                  </div>

                  <div className="space-y-2.5 text-xs text-slate-700 dark:text-slate-300 mb-6">
                    <div className="flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                      <span>Dedicated Technical Lead & Fullstack Engineer</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                      <span>Bespoke frontend UX tailored to internal staff workflows</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                      <span>Payment gateway, WhatsApp & third-party webhook sync</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                      <span>100% source code repository & Dockerized VPS deployment</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                      <span>30-Day Post-Launch Warranty & Bug-Fix SLA</span>
                    </div>
                  </div>
                </div>

                <a
                  href={`https://wa.me/919880893416?text=Hi%20Johnson%20Softwares,%20we%20want%20to%20discuss%20a%20Custom%20Implementation%20project.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-2.5 px-4 rounded-xl text-xs font-bold text-center bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white hover:bg-slate-200 dark:hover:bg-slate-700 transition-all flex items-center justify-center gap-1.5"
                >
                  Consult on Custom Build
                  <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </div>

              {/* Bespoke Tier 2: Enterprise Platform */}
              <div className="bg-white dark:bg-slate-900 border-2 border-amber-500/50 rounded-2xl p-6 shadow-xl relative flex flex-col justify-between">
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-full shadow-md">
                  FULL-SCALE ARCHITECTURE
                </div>

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-400">
                      Multi-Branch / Mission-Critical
                    </span>
                    <span className="text-xs font-bold text-slate-500">2 to 6 Months</span>
                  </div>

                  <h4 className="text-xl font-bold text-slate-900 dark:text-white mb-1">
                    Enterprise Platform
                  </h4>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-4 min-h-[36px]">
                    Complex multi-entity ERPs, algorithmic fintech & trading engines, high-throughput message brokers, and regulatory compliance.
                  </p>

                  <div className="pb-4 mb-4 border-b border-slate-100 dark:border-slate-800">
                    <span className="text-xs text-slate-400 font-semibold uppercase">Project Investment</span>
                    <div className="text-2xl font-black text-slate-900 dark:text-white mt-1">
                      {isINR ? "₹10,00,000 – ₹50,00,000+" : isAED ? "AED 45,000 – AED 2,20,000+" : "$12,000 – $60,000+"}
                    </div>
                    <span className="text-[11px] text-slate-500 dark:text-slate-400">Phased milestone roadmap with SLA</span>
                  </div>

                  <div className="space-y-2.5 text-xs text-slate-700 dark:text-slate-300 mb-6">
                    <div className="flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                      <span>Dedicated Delivery Pod (Architect, Fullstack, Mobile, QA)</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                      <span>High-availability clustering & sub-60s active-passive failover</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                      <span>SOC2-ready audit trail & role-based cryptographic permissions</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                      <span>Private AWS/GCP or hybrid on-premises deployment</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                      <span>60-Day Hypercare On-Call Warranty SLA</span>
                    </div>
                  </div>
                </div>

                <a
                  href={`https://wa.me/919880893416?text=Hi%20Johnson%20Softwares,%20we%20would%20like%20an%20Executive%20Architect%20consultation%20for%20an%20Enterprise%20Platform.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-2.5 px-4 rounded-xl text-xs font-bold text-center bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 hover:opacity-95 shadow-md transition-all flex items-center justify-center gap-1.5"
                >
                  Consult with Principal Architect
                  <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </div>

              {/* Bespoke Tier 3: Annual SLA & AMC */}
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                      Ongoing Support
                    </span>
                    <span className="text-xs font-bold text-slate-500">Annual Retainer</span>
                  </div>

                  <h4 className="text-xl font-bold text-slate-900 dark:text-white mb-1">
                    Annual SLA & Engineering AMC
                  </h4>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-4 min-h-[36px]">
                    Guaranteed level-3 incident response, continuous security patching, framework upgrades, and reserved sprint capacity.
                  </p>

                  <div className="pb-4 mb-4 border-b border-slate-100 dark:border-slate-800">
                    <span className="text-xs text-slate-400 font-semibold uppercase">Annual Rate</span>
                    <div className="text-2xl font-black text-slate-900 dark:text-white mt-1">
                      15% – 25%
                    </div>
                    <span className="text-[11px] text-slate-500 dark:text-slate-400">Of total implementation value per year</span>
                  </div>

                  <div className="space-y-2.5 text-xs text-slate-700 dark:text-slate-300 mb-6">
                    <div className="flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                      <span>1-Hour Critical Incident Response SLA</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                      <span>Dedicated VPS / Cloud infrastructure health monitoring</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                      <span>Monthly security audits & dependency vulnerability patching</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                      <span>Allocated developer hours each month for minor enhancements</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                      <span>Quarterly architectural review & performance tuning</span>
                    </div>
                  </div>
                </div>

                <a
                  href={`https://wa.me/919880893416?text=Hi%20Johnson%20Softwares,%20we%20want%20to%20inquire%20about%20Annual%20Support%20and%20SLA%20packages.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-2.5 px-4 rounded-xl text-xs font-bold text-center bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white hover:bg-slate-200 dark:hover:bg-slate-700 transition-all flex items-center justify-center gap-1.5"
                >
                  Inquire SLA Terms
                  <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          </section>

          {/* Project Qualification Rubric */}
          <section className="py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
            <div className="bg-slate-900 border border-slate-800 rounded-3xl p-8 sm:p-10 shadow-xl">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-8 pb-6 border-b border-slate-800">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/20 text-primary text-xs font-bold uppercase tracking-wider mb-2">
                    <Scale className="w-3.5 h-3.5" />
                    Transparent Evaluation Framework
                  </div>
                  <h3 className="text-2xl font-extrabold text-white">
                    Project Qualification Rubric (10-Point Decision Matrix)
                  </h3>
                  <p className="mt-1 text-xs sm:text-sm text-slate-400 max-w-2xl">
                    How our Solution Architect evaluates prospective custom projects to guarantee execution feasibility and protect client investments.
                  </p>
                </div>

                <div className="flex items-center gap-2 bg-slate-800 border border-slate-700 px-4 py-2 rounded-xl text-xs font-mono text-slate-300">
                  <ShieldCheck className="w-4 h-4 text-emerald-400" />
                  Objective Criteria · Zero Guesswork
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-slate-950/60 border border-emerald-500/30 p-5 rounded-2xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-black uppercase text-emerald-400">Class A (8–10 pts)</span>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-300">Direct Lead</span>
                  </div>
                  <h5 className="text-sm font-bold text-white mb-2">Immediate SOW Execution</h5>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Well-defined PRD, realistic ₹5L+ budget, engaged CTO/founder, feasible 6–12 week timeline. Solution Architect handles project directly.
                  </p>
                </div>

                <div className="bg-slate-950/60 border border-blue-500/30 p-5 rounded-2xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-black uppercase text-blue-400">Class B (6–7 pts)</span>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-blue-500/10 text-blue-300">Delivery Network</span>
                  </div>
                  <h5 className="text-sm font-bold text-white mb-2">Vetted Pod Assignment</h5>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Clear functional requirements, moderate budget (₹2L–₹5L). Assembled from our vetted engineering delivery network under Johnson Softwares PM.
                  </p>
                </div>

                <div className="bg-slate-950/60 border border-amber-500/30 p-5 rounded-2xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-black uppercase text-amber-400">Class C (4–5 pts)</span>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-amber-500/10 text-amber-300">Phase 0 First</span>
                  </div>
                  <h5 className="text-sm font-bold text-white mb-2">Mandatory Paid Discovery</h5>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Vague requirements or conceptual features. Client must complete Phase 0 Architecture before any fixed-bid implementation quote can be issued.
                  </p>
                </div>

                <div className="bg-slate-950/60 border border-rose-500/30 p-5 rounded-2xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-black uppercase text-rose-400">Class D (0–3 pts)</span>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-rose-500/10 text-rose-300">Model A Referral</span>
                  </div>
                  <h5 className="text-sm font-bold text-white mb-2">Referred to SaaS Tiers</h5>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Sub-₹1L expectations for custom platforms or unrealistic 5-day deadlines. Guided to our off-the-shelf Model A SaaS CRM plans to protect client budget.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Model A vs Model B Comparative Matrix Table */}
          <section className="py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-8 shadow-sm">
              <div className="text-center mb-8">
                <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white">
                  Side-by-Side Model Comparison
                </h3>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Select the commercial framework that matches your operational requirements and timeline.
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs sm:text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-800">
                      <th className="py-3 px-4 font-bold text-slate-400 uppercase tracking-wider text-xs">Dimension</th>
                      <th className="py-3 px-4 font-bold text-primary text-sm sm:text-base">Model A: CRM SaaS Platform</th>
                      <th className="py-3 px-4 font-bold text-amber-500 text-sm sm:text-base">Model B: Bespoke Enterprise</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-slate-700 dark:text-slate-300">
                    <tr>
                      <td className="py-3.5 px-4 font-bold text-slate-900 dark:text-white">Commercial Structure</td>
                      <td className="py-3.5 px-4">Predictable monthly retainer (From ₹2,499/mo)</td>
                      <td className="py-3.5 px-4">Phase 0 blueprint + fixed-milestone deliverables</td>
                    </tr>
                    <tr>
                      <td className="py-3.5 px-4 font-bold text-slate-900 dark:text-white">IP & Source Code</td>
                      <td className="py-3.5 px-4">Software subscription with full data portability (SQLite export)</td>
                      <td className="py-3.5 px-4 font-bold text-emerald-600 dark:text-emerald-400">100% exclusive client IP & source code repository assignment</td>
                    </tr>
                    <tr>
                      <td className="py-3.5 px-4 font-bold text-slate-900 dark:text-white">Turnaround / Deployment</td>
                      <td className="py-3.5 px-4 font-bold text-emerald-600 dark:text-emerald-400">48 to 72 Hours Turnkey</td>
                      <td className="py-3.5 px-4">4 to 12 Weeks (Phase 0: 1–2 wks; Build: 4–8 wks)</td>
                    </tr>
                    <tr>
                      <td className="py-3.5 px-4 font-bold text-slate-900 dark:text-white">Customization Scope</td>
                      <td className="py-3.5 px-4">Dynamic industry schemas, custom fields, webhooks, Kanban</td>
                      <td className="py-3.5 px-4">Arbitrary bespoke logic, custom algorithms, proprietary DB schema</td>
                    </tr>
                    <tr>
                      <td className="py-3.5 px-4 font-bold text-slate-900 dark:text-white">Engineering Team</td>
                      <td className="py-3.5 px-4">Automated self-serve + Standard ticket/WhatsApp SLA</td>
                      <td className="py-3.5 px-4">Dedicated Solution Architect + Vetted Engineering Pod</td>
                    </tr>
                    <tr>
                      <td className="py-3.5 px-4 font-bold text-slate-900 dark:text-white">Infrastructure</td>
                      <td className="py-3.5 px-4">Managed Docker containers with automated cloud backup</td>
                      <td className="py-3.5 px-4">Dedicated client AWS/GCP or VPS with Terraform/IAC & CI/CD</td>
                    </tr>
                    <tr>
                      <td className="py-3.5 px-4 font-bold text-slate-900 dark:text-white">Ideal For</td>
                      <td className="py-3.5 px-4">Sales teams, real estate brokers, clinics, fast deployment</td>
                      <td className="py-3.5 px-4">Fintechs, algorithmic brokerages, custom ERPs, legacy migrations</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </>
      )}

      {/* Online Cashfree Checkout Modal */}
      {checkoutModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl max-w-lg w-full p-6 sm:p-8 shadow-2xl relative">
            <button
              onClick={() => setCheckoutModalOpen(false)}
              className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="mb-4">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs font-bold uppercase tracking-wider mb-2">
                <CreditCard className="w-3.5 h-3.5" />
                Turnkey CRM Activation
              </div>
              <h3 className="text-xl sm:text-2xl font-extrabold text-slate-900 dark:text-white">
                Deploy {PLANS[selectedCheckoutPlan]?.name} Plan
              </h3>
              <p className="text-xs text-slate-500 mt-1">
                Choose your preferred payment method. Official GST Tax Invoice is dispatched immediately upon activation.
              </p>
            </div>

            {/* Payment Method Switcher Tabs */}
            <div className="grid grid-cols-2 gap-2 p-1 bg-slate-100 dark:bg-slate-800 rounded-xl mb-4 text-xs font-bold">
              <button
                type="button"
                onClick={() => setCheckoutTab("gateway")}
                className={`py-2 px-3 rounded-lg flex items-center justify-center gap-1.5 transition-all ${
                  checkoutTab === "gateway"
                    ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-xs"
                    : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                }`}
              >
                <CreditCard className="w-3.5 h-3.5 text-emerald-500" />
                <span>Card / NetBanking</span>
              </button>
              <button
                type="button"
                onClick={() => setCheckoutTab("upi")}
                className={`py-2 px-3 rounded-lg flex items-center justify-center gap-1.5 transition-all ${
                  checkoutTab === "upi"
                    ? "bg-white dark:bg-slate-900 text-emerald-600 dark:text-emerald-400 shadow-xs"
                    : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                }`}
              >
                <QrCode className="w-3.5 h-3.5 text-emerald-500" />
                <span>Direct UPI &amp; QR</span>
              </button>
            </div>

            {/* Price Summary */}
            <div className="bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/60 rounded-2xl p-4 mb-4 space-y-1.5 text-xs">
              <div className="flex justify-between text-slate-600 dark:text-slate-300">
                <span>Monthly Retainer:</span>
                <span className="font-bold text-slate-900 dark:text-white">
                  ₹{PLANS[selectedCheckoutPlan]?.pricing.INR.monthly.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between text-slate-600 dark:text-slate-300">
                <span>Implementation &amp; Onboarding:</span>
                <span className="font-bold text-slate-900 dark:text-white">
                  ₹{PLANS[selectedCheckoutPlan]?.pricing.INR.setup.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between text-slate-600 dark:text-slate-300 border-t border-slate-200 dark:border-slate-700 pt-1.5">
                <span>GST (18% CGST + SGST):</span>
                <span className="font-semibold text-slate-700 dark:text-slate-300">
                  ₹
                  {Math.round(
                    (PLANS[selectedCheckoutPlan]?.pricing.INR.monthly +
                      PLANS[selectedCheckoutPlan]?.pricing.INR.setup) *
                      0.18
                  ).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between text-sm font-black text-slate-900 dark:text-white border-t border-slate-200 dark:border-slate-700 pt-1.5">
                <span>Total Amount Due (Incl. 18% GST):</span>
                <span className="text-emerald-600 dark:text-emerald-400 text-base">
                  ₹
                  {Math.round(
                    (PLANS[selectedCheckoutPlan]?.pricing.INR.monthly +
                      PLANS[selectedCheckoutPlan]?.pricing.INR.setup) *
                      1.18
                  ).toLocaleString()}
                </span>
              </div>
            </div>

            {checkoutError && (
              <div className="p-3 mb-4 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-300 text-xs">
                {checkoutError}
              </div>
            )}

            {/* TAB 1: Online Gateway (Cashfree) */}
            {checkoutTab === "gateway" && (
              <>
                <div className="space-y-3 mb-4">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase mb-1">
                      Company / Contact Name *
                    </label>
                    <input
                      type="text"
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                      placeholder="e.g. Acme Corp / Alex"
                      className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs focus:ring-2 focus:ring-primary outline-none"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase mb-1">
                        Phone / WhatsApp *
                      </label>
                      <input
                        type="tel"
                        value={clientPhone}
                        onChange={(e) => setClientPhone(e.target.value)}
                        placeholder="9880893416"
                        className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs focus:ring-2 focus:ring-primary outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase mb-1">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={clientEmail}
                        onChange={(e) => setClientEmail(e.target.value)}
                        placeholder="billing@company.com"
                        className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs focus:ring-2 focus:ring-primary outline-none"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <button
                    type="button"
                    disabled={checkoutLoading}
                    onClick={handleProcessCashfreePayment}
                    className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-bold text-xs flex items-center justify-center gap-2 hover:opacity-95 shadow-md shadow-emerald-600/20 transition-all disabled:opacity-50 cursor-pointer"
                  >
                    {checkoutLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Launching Cashfree Gateway...</span>
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4" />
                        <span>Pay with Cashfree (Cards / NetBanking)</span>
                      </>
                    )}
                  </button>

                  <div className="text-center pt-2">
                    <button
                      type="button"
                      onClick={() => setCheckoutTab("upi")}
                      className="text-[11px] font-bold text-primary hover:underline inline-flex items-center gap-1 cursor-pointer"
                    >
                      <QrCode className="w-3.5 h-3.5" />
                      Switch to Direct 0-Fee UPI Quick Pay
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* TAB 2: Direct UPI & Bank Remittance */}
            {checkoutTab === "upi" && (
              <div className="space-y-4">
                <div className="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/30">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300 flex items-center gap-1.5">
                      <QrCode className="w-4 h-4" /> Official Business UPI VPA
                    </span>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-800 dark:text-emerald-200">
                      0% Transaction Fee
                    </span>
                  </div>

                  <div className="flex items-center justify-between bg-white dark:bg-slate-900 border border-emerald-500/30 rounded-xl px-3 py-2.5 mb-3">
                    <code className="text-sm font-black text-slate-900 dark:text-white font-mono">
                      johnsondev02@ybl
                    </code>
                    <button
                      type="button"
                      onClick={handleCopyUpi}
                      className="px-2.5 py-1 text-xs font-bold rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white flex items-center gap-1 shadow-xs transition-colors cursor-pointer"
                    >
                      {copiedUpi ? (
                        <>
                          <Check className="w-3.5 h-3.5" />
                          <span>Copied</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-3.5 h-3.5" />
                          <span>Copy</span>
                        </>
                      )}
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <a
                      href={`upi://pay?pa=johnsondev02@ybl&pn=Johnson%20Softwares&am=${Math.round(
                        (PLANS[selectedCheckoutPlan]?.pricing.INR.monthly +
                          PLANS[selectedCheckoutPlan]?.pricing.INR.setup) *
                          1.18
                      )}&cu=INR&tn=${encodeURIComponent(
                        `Johnson Softwares ${PLANS[selectedCheckoutPlan]?.name} CRM Plan`
                      )}`}
                      className="py-2.5 px-3 rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-950 font-bold text-xs flex items-center justify-center gap-1.5 shadow-md hover:opacity-90 transition-all text-center"
                    >
                      <span>Pay via UPI App (GPay/PhonePe)</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </a>

                    <a
                      href={`https://wa.me/919880893416?text=${encodeURIComponent(
                        `Hi Johnson Softwares, I am completing payment for the ${
                          PLANS[selectedCheckoutPlan]?.name
                        } CRM plan.\n\nTotal Amount: ₹${Math.round(
                          (PLANS[selectedCheckoutPlan]?.pricing.INR.monthly +
                            PLANS[selectedCheckoutPlan]?.pricing.INR.setup) *
                            1.18
                        ).toLocaleString()} (incl. 18% GST)\nCompany/Name: ${
                          clientName.trim() || "My Business"
                        }\nPhone: ${clientPhone.trim() || ""}\nEmail: ${
                          clientEmail.trim() || ""
                        }\n\nPlease confirm UPI payment and send CRM onboarding link.`
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="py-2.5 px-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs flex items-center justify-center gap-1.5 shadow-md transition-all text-center"
                    >
                      <MessageSquare className="w-3.5 h-3.5" />
                      <span>Confirm on WhatsApp</span>
                    </a>
                  </div>
                </div>

                {/* Bank Transfer Details Accordion */}
                <div className="border border-slate-200 dark:border-slate-800 rounded-xl p-3 text-xs">
                  <button
                    type="button"
                    onClick={() => setShowBankDetails(!showBankDetails)}
                    className="w-full flex items-center justify-between text-slate-700 dark:text-slate-300 font-bold hover:text-slate-900 dark:hover:text-white cursor-pointer"
                  >
                    <span>Direct Bank NEFT / IMPS Remittance</span>
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${
                        showBankDetails ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {showBankDetails && (
                    <div className="mt-2.5 pt-2.5 border-t border-slate-200 dark:border-slate-800 space-y-1 text-slate-600 dark:text-slate-400 font-mono text-[11px]">
                      <div>• Beneficiary: Johnson D / Johnson Softwares</div>
                      <div>• Bank: HDFC Bank Ltd</div>
                      <div>• Account Number: 50100455367410</div>
                      <div>• IFSC Code: HDFC0004895</div>
                      <div>• Branch: HSR Layout, Bangalore</div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Comprehensive FAQs Accordion */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs font-bold uppercase tracking-wider mb-2">
            <HelpCircle className="w-3.5 h-3.5 text-primary" />
            Commercial Transparency
          </div>
          <h2 className="text-3xl font-extrabold text-slate-900 dark:text-white">
            Frequently Asked Commercial & Technical Questions
          </h2>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            Clear answers about subscription tiers, bespoke engineering, IP ownership, carrier billing, and SLAs.
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => {
            const isOpen = openFaq === index;
            return (
              <div
                key={index}
                className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden shadow-sm transition-all"
              >
                <button
                  onClick={() => setOpenFaq(isOpen ? null : index)}
                  className="w-full p-5 text-left flex items-center justify-between gap-4 font-bold text-slate-900 dark:text-white text-sm sm:text-base hover:text-primary transition-colors"
                >
                  <span>{faq.q}</span>
                  <ChevronDown
                    className={`w-4 h-4 text-slate-400 transition-transform duration-200 shrink-0 ${
                      isOpen ? "rotate-180 text-primary" : ""
                    }`}
                  />
                </button>
                {isOpen && (
                  <div className="px-5 pb-5 text-xs sm:text-sm text-slate-600 dark:text-slate-400 leading-relaxed border-t border-slate-100 dark:border-slate-800 pt-3">
                    {faq.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center border-t border-slate-200 dark:border-slate-800">
        <h2 className="text-3xl font-extrabold text-slate-900 dark:text-white">
          Ready to Modernize Your Software Architecture?
        </h2>
        <p className="mt-3 text-sm text-slate-500 dark:text-slate-400 max-w-xl mx-auto">
          Whether you need instant 72-hour CRM SaaS deployment or a custom enterprise platform blueprint, our architecture team is ready to assist.
        </p>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
          <a
            href="https://wa.me/919880893416?text=Hi%20Johnson%20Softwares,%20we%20want%20to%20schedule%20a%20commercial%20architecture%20review%20call."
            target="_blank"
            rel="noopener noreferrer"
            className="px-6 py-3 rounded-xl bg-primary text-white text-xs font-bold flex items-center gap-2 hover:bg-primary/90 shadow-md shadow-primary/20 transition-all"
          >
            <PhoneCall className="w-4 h-4" />
            Schedule 15-Minute Architect Call
          </a>

          <Link
            href="/crm"
            className="px-6 py-3 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white text-xs font-bold hover:bg-slate-200 dark:hover:bg-slate-700 transition-all"
          >
            Launch Interactive CRM Portal
          </Link>
        </div>
      </section>
    </div>
  );
}
